// Hero met slides: vegen (scroll-snap), balkjes, pijlen en automatisch afspelen met pauzeknop.
// Automatisch afspelen stopt bij aanwijzen, aantikken, toetsenbordfocus, buiten beeld en bij "minder beweging".

class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.bars = [...this.querySelectorAll('[data-go]')];
    this.toggleButton = this.querySelector('[data-toggle]');
    if (!this.track || this.slides.length < 2) return;

    this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.duration = Math.max(3, Number(this.dataset.speed) || 6.5) * 1000;
    this.index = 0;
    this.elapsed = 0;
    this.holds = new Set();

    const autoplay = this.dataset.autoplay === 'true';
    if (!autoplay) this.setAttribute('data-static', '');
    this.playing = autoplay && !this.reduce;
    this.syncToggle();

    this.bars.forEach((bar) => bar.addEventListener('click', () => this.go(Number(bar.dataset.go))));
    this.querySelectorAll('[data-step]').forEach((button) =>
      button.addEventListener('click', () => this.go(this.index + Number(button.dataset.step)))
    );
    this.toggleButton?.addEventListener('click', () => {
      this.playing = !this.playing;
      this.syncToggle();
    });

    const hold = (reason, on) => (on ? this.holds.add(reason) : this.holds.delete(reason));
    this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && hold('hover', true));
    this.addEventListener('pointerleave', () => hold('hover', false));
    this.addEventListener('focusin', () => hold('focus', true));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) hold('focus', false);
    });
    this.track.addEventListener('touchstart', () => hold('touch', true), { passive: true });
    this.track.addEventListener('touchend', () => hold('touch', false), { passive: true });
    this.onVisibility = () => hold('hidden', document.hidden);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.observer = new IntersectionObserver(([entry]) => hold('offscreen', !entry.isIntersecting));
    this.observer.observe(this);
    this.onBlockSelect = (event) => {
      const index = this.slides.indexOf(event.target);
      if (index < 0) return;
      this.playing = false;
      this.syncToggle();
      this.go(index);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    let settle;
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(settle);
        settle = setTimeout(() => {
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (index !== this.index) this.setIndex(index);
        }, 120);
      },
      { passive: true }
    );

    this.setIndex(0);
    this.last = performance.now();
    this.frame = requestAnimationFrame((time) => this.tick(time));
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  syncToggle() {
    this.toggleAttribute('data-paused', !this.playing);
    this.track.setAttribute('aria-live', this.playing ? 'off' : 'polite');
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-label', this.playing ? this.dataset.labelPause : this.dataset.labelPlay);
    }
    this.paint();
  }

  tick(time) {
    const delta = time - this.last;
    this.last = time;
    if (this.playing && this.holds.size === 0) {
      this.elapsed += delta;
      if (this.elapsed >= this.duration) this.go(this.index + 1);
      else this.paint();
    }
    this.frame = requestAnimationFrame((next) => this.tick(next));
  }

  go(index) {
    const count = this.slides.length;
    const target = (index + count) % count;
    this.track.scrollTo({ left: this.track.clientWidth * target, behavior: this.reduce ? 'auto' : 'smooth' });
    this.setIndex(target);
  }

  setIndex(index) {
    this.index = index;
    this.elapsed = 0;
    this.slides.forEach((slide, i) => {
      slide.inert = i !== index;
    });
    const next = this.slides[(index + 1) % this.slides.length]?.querySelector('img[loading="lazy"]');
    if (next) next.loading = 'eager';
    this.paint();
  }

  paint() {
    const progress = this.playing ? Math.min(1, this.elapsed / this.duration) : 1;
    this.bars.forEach((bar, i) => {
      bar.classList.toggle('is-done', i < this.index);
      if (i === this.index) bar.setAttribute('aria-current', 'true');
      else bar.removeAttribute('aria-current');
      bar.querySelector('i')?.style.setProperty('--p', i === this.index ? progress : 0);
    });
  }
}

if (!customElements.get('hero-slideshow')) customElements.define('hero-slideshow', HeroSlideshow);
